import * as angular from 'angular';
import * as template from './template.html';
import './styling.scss';
import { CurrentUserService } from 'services/currentUser';
import {
    getFormattedExtendedReplacementCostCoverageAmount,
    getFormattedHomeCoverageAmount,
    getFormattedLossOfUseCoverageAmount,
    getFormattedOrdinanceProtectionCoverageAmount,
    getFormattedSeparateStructureCoverageAmount,
    getFoundationAndWaterCoverageDisplay,
    getMineSubsidenceCoverageDisplay,
    getRoofCoverageDisplay,
    getSinkholeCollapseCoverageDisplay
} from 'common/source/utilities/policyInfoDisplay';
import { isCondoPolicy, isDp3Policy, isHomePolicy } from 'common/source/utilities/policyInfoHelper';

const module = angular.module('coverage-home-section', []);

interface HomeCoverage extends NameAndValue {
    tooltipContent?: string;
}

interface CoverageHomeSectionController extends angular.IStrictController {
    title: string;
    icon: string;
    description: string;
    coverages: Array<HomeCoverage>;
}

module.component('coverageHomeSection', {
    template: template,
    controller: function(
        this: CoverageHomeSectionController,
        currentUserService: CurrentUserService,
        $stateParams: PolicyStateParams
    ) {
        'ngInject';

        this.$onInit = () => {
            const policyIndex = $stateParams.policyIndex;
            this.title = 'Home';
            this.icon = 'house';

            currentUserService.getCustomerPolicyInfoByIndex(policyIndex).then((policyInfo) => {
                const isHome = isHomePolicy(policyInfo);
                const isCondo = isCondoPolicy(policyInfo);
                const isDp3 = isDp3Policy(policyInfo);

                this.coverages = [];

                const homeCoverageAmount = getFormattedHomeCoverageAmount(policyInfo);

                if (isHome) {
                    this.description = `This is the insurance that specifically covers your home and any secondary
                                        structures on your property. It does not include the coverage for your
                                        belongings or personal liability.`;
                    this.coverages.push({
                        name: 'Home Rebuilding Coverage',
                        value: homeCoverageAmount
                    });
                } else if (isCondo) {
                    this.description = `This is the insurance that covers repair or replacement costs for items of your
                                        condo which you are responsible for. This typically includes things like
                                        flooring, fixtures, inner walls, cabinets and counters.`;
                    this.coverages.push({
                        name: 'Condo Damage Coverage',
                        value: homeCoverageAmount
                    });
                } else if (isDp3) {
                    this.description = `This is the insurance that specifically covers your property and any secondary 
                                        structures. It does not include the coverage for your belongings or personal 
                                        liability.`;
                    this.coverages.push({
                        name: 'Home Rebuilding Coverage',
                        value: homeCoverageAmount
                    });
                }

                const extendedReplacementCostCoverageAmount = getFormattedExtendedReplacementCostCoverageAmount(
                    policyInfo
                );
                if (extendedReplacementCostCoverageAmount) {
                    this.coverages.push({
                        name: 'Extended Replacement Cost',
                        value: extendedReplacementCostCoverageAmount
                    });
                }

                const separateStructureCoverageAmount = getFormattedSeparateStructureCoverageAmount(policyInfo);
                if (separateStructureCoverageAmount) {
                    this.coverages.push({
                        name: 'Separate Structure',
                        value: separateStructureCoverageAmount
                    });
                }

                const lossOfUseCoverageAmount = getFormattedLossOfUseCoverageAmount(policyInfo);
                let lossOfUseTooltipContent = '';

                if (isDp3) {
                    lossOfUseTooltipContent = `In the event that your home becomes uninhabitable, this coverage reimburses 
                                               you for lost rental income (based on market fair rental value), while the 
                                               property is being repaired.`;
                }

                if (lossOfUseCoverageAmount) {
                    this.coverages.push({
                        name: 'Loss of Use',
                        value: lossOfUseCoverageAmount,
                        tooltipContent: lossOfUseTooltipContent
                    });
                }

                const roofCoverageDisplay = getRoofCoverageDisplay(policyInfo);
                if (roofCoverageDisplay) {
                    this.coverages.push({
                        name: 'Roof Surface Coverage',
                        value: roofCoverageDisplay,
                        tooltipContent: `Your roof may either be insured for its Actual Cash Value (depreciated value
                                         based on its age) or its Replacement Cost (the cost of repairing or replacing
                                         it today) determined based on the roof's age and materials.`
                    });
                }

                const ordinanceProtectionCoverageAmount = getFormattedOrdinanceProtectionCoverageAmount(policyInfo);
                if (ordinanceProtectionCoverageAmount) {
                    this.coverages.push({
                        name: 'Ordinance Protection',
                        value: ordinanceProtectionCoverageAmount
                    });
                }

                const mineSubsidenceCoverageDisplay = getMineSubsidenceCoverageDisplay(policyInfo);
                if (mineSubsidenceCoverageDisplay) {
                    this.coverages.push({
                        name: 'Mine Subsidence',
                        value: mineSubsidenceCoverageDisplay
                    });
                }

                const sinkholeCollapseCoverageDisplay = getSinkholeCollapseCoverageDisplay(policyInfo);
                if (sinkholeCollapseCoverageDisplay) {
                    this.coverages.push({
                        name: 'Sinkhole Collapse',
                        value: sinkholeCollapseCoverageDisplay
                    });
                }

                const foundationAndWaterCoverageDisplay = getFoundationAndWaterCoverageDisplay(policyInfo);
                if (foundationAndWaterCoverageDisplay) {
                    this.coverages.push({
                        name: 'Foundation and Water Coverage',
                        value: foundationAndWaterCoverageDisplay
                    });
                }
            });
        };
    }
});

export { module };
